import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Offre() {
  return (
    <div className="offre-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="section-container">
          <span className="section-label">Offre</span>
          <h1 className="page-title">Nos champs d'intervention</h1>
          <p className="page-subtitle">
            Open Cortex intervient sur les fondations opérationnelles des organisations : compréhension du fonctionnement réel, structuration des processus et mise en place de leviers concrets pour accompagner leur évolution. Cette approche permet aux dirigeants de disposer d'une vision claire de leur fonctionnement et des leviers actionnables pour piloter leur évolution.
          </p>
        </div>
      </section>

      {/* Interventions Section */}
      <section className="interventions-section">
        <div className="section-container">
          <div className="interventions-grid">
            <div className="intervention-card">
              <div className="intervention-header">
                <h3>Audit et diagnostic opérationnel</h3>
              </div>
              <div className="intervention-content">
                <h4>Ce que nous faisons</h4>
                <p>
                  Nous analysons votre fonctionnement opérationnel tel qu'il est réellement pratiqué, afin d'en dégager les points structurants, les zones de tension et les leviers d'amélioration prioritaires. Cette clarification permet de prendre des décisions sur des bases documentées et partagées.
                </p>
                <h4>Ce que vous obtenez</h4>
                <ul>
                  <li>Une cartographie claire et partagée de vos processus clés</li>
                  <li>Une lecture structurée des points d'attention et des dépendances critiques</li>
                  <li>Des axes d'action hiérarchisés pour fluidifier l'exécution et éclairer les décisions</li>
                  <li>Une base de travail permettant de réduire les frictions internes et le temps consacré aux arbitrages opérationnels</li>
                </ul>
              </div>
            </div>

            <div className="intervention-card">
              <div className="intervention-header">
                <h3>Structuration des processus</h3>
              </div>
              <div className="intervention-content">
                <h4>Ce que nous faisons</h4>
                <p>
                  Nous formalisons vos processus opérationnels de manière rigoureuse, documentée et directement utilisable par vos équipes. Cette structuration renforce la cohérence de vos opérations et facilite leur continuité, y compris en cas de changement d'équipe ou de montée en charge. Cette formalisation renforce la capacité à déléguer, à former et à faire évoluer les opérations sans rupture.
                </p>
                <h4>Ce que vous obtenez</h4>
                <ul>
                  <li>Des processus documentés, clairs et transmissibles dans la durée</li>
                  <li>Des fiches de procédures opérationnelles directement exploitables</li>
                  <li>Un cadre de référence partagé, facilitant l'alignement et les arbitrages</li>
                  <li>Une base structurée permettant d'identifier les leviers d'automatisation et de faire évoluer vos opérations sans friction</li>
                  <li>Une capacité renforcée à passer à l'échelle et à intégrer de nouveaux outils ou de l'IA de manière maîtrisée</li>
                </ul>
              </div>
            </div>

            <div className="intervention-card">
              <div className="intervention-header">
                <h3>Automatisation de workflows ciblés</h3>
              </div>
              <div className="intervention-content">
                <h4>Ce que nous faisons</h4>
                <p>
                  Nous identifions les flux opérationnels dont l'automatisation apporte une valeur mesurable : coordination entre étapes, priorisation des actions, continuité en cas d'absence, aide à la qualification et à la décision. Nous concevons ensuite des automatisations adaptées à votre maturité et à votre contexte. Cette approche libère du temps décisionnel et renforce la réactivité face aux variations d'activité.
                </p>
                <h4>Ce que vous obtenez</h4>
                <ul>
                  <li>Des workflows automatisés, documentés et évolutifs</li>
                  <li>Du temps libéré sur les tâches à faible valeur ajoutée, réalloué vers des activités à plus fort impact</li>
                  <li>Une continuité opérationnelle renforcée, moins dépendante des disponibilités individuelles</li>
                  <li>Une capacité à traiter des volumes croissants sans augmentation proportionnelle des ressources</li>
                  <li>Un transfert de compétences pour maintenir et faire évoluer les automatisations en interne</li>
                </ul>
                <h4>Précisions</h4>
                <p className="intervention-note">
                  L'automatisation s'appuie sur une structuration préalable des processus. Une base claire permet des automatisations fiables et maintenables dans la durée.
                </p>
              </div>
            </div>

            <div className="intervention-card">
              <div className="intervention-header">
                <h3>IA appliquée aux usages métier</h3>
              </div>
              <div className="intervention-content">
                <h4>Ce que nous faisons</h4>
                <p>
                  Nous identifions les cas d'usage où l'intelligence artificielle peut réellement apporter de la valeur (classification, analyse, aide à la décision) et nous concevons des solutions adaptées. Cette démarche permet d'intégrer l'IA comme outil d'aide à la décision, dans un cadre maîtrisé et durable.
                </p>
                <h4>Ce que vous obtenez</h4>
                <ul>
                  <li>Une identification des usages pertinents de l'IA</li>
                  <li>Des solutions IA intégrées à vos processus</li>
                  <li>Une documentation des limites et des précautions d'usage</li>
                </ul>
                <h4>Précisions</h4>
                <p className="intervention-note">
                  L'IA n'est pas magique. Elle nécessite des données de qualité, un cadre d'usage clair et une compréhension de ses limites. Nous ne promettons pas de résultats garantis, mais une approche raisonnée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="section-container">
          <div className="pricing-card">
            <div className="pricing-header">
              <h3>Tarifs & Modalités</h3>
            </div>
            <div className="pricing-content">
              <p>
                Chaque intervention est construite sur mesure, après un premier échange permettant de clarifier vos besoins, votre contexte et vos priorités. Le périmètre, les livrables et les modalités sont formalisés dans un devis détaillé avant tout engagement.
              </p>
              <div className="pricing-baseline">
                <p className="pricing-amount">Interventions à partir de 2 500 € HT</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <section className="framework-section">
        <div className="section-container">
          <div className="framework-content">
            <h2>Cadre de collaboration</h2>
            <p className="lead-text">
              Open Cortex intervient selon une méthode structurée, mobilisant expertise, rigueur et expérience du terrain pour accompagner l'évolution de vos opérations. Chaque mission repose sur un périmètre clairement défini et des livrables explicites. Ce cadre structuré facilite le pilotage de la mission et la prise de décisions d'ajustement en cours de route.
            </p>
            <p>
              Avant tout engagement, un devis détaillé formalise le cadre d'intervention : objectifs, étapes, livrables attendus et modalités pratiques. Cette clarté initiale permet un travail serein et des échanges réguliers tout au long de la mission.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Discutons de votre contexte</h2>
            <p>Un premier échange permet de comprendre vos besoins et de déterminer si notre approche vous convient.</p>
            <Link to="/contact">
              <Button className="btn-primary btn-large">
                Prendre contact <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}